/**
 * The rail's footer: a deck of background-work cards, one visible at rest.
 *
 * The old card listed everything that was running and hid itself when nothing
 * was - which meant the one surface whose job is to say "the machinery is
 * fine" said nothing at all, almost always, and said the same nothing when the
 * machinery was dead. This shows the single most important card face up, with
 * the rest peeking behind it, and fans the deck out on hover.
 *
 * Why the geometry is arithmetic rather than layout: at rest the cards overlap,
 * expanded they sit in a column, and animating between those two with real
 * layout means animating `height` and `position` - which do not interpolate.
 * So every card is absolutely positioned and only `transform` and `opacity`
 * move, both of which the compositor can do at 60fps. That requires every card
 * to be the same height, which `JobFace` guarantees by construction.
 */
import { useState } from "react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { useSignals } from "../SignalsProvider";
import { buildCards } from "./cards";
import { JobFace } from "./JobFace";

/** Card height and the gap between fanned cards, in px. Mirrors the CSS. */
const CARD_H = 56;
const GAP = 8;
/** How far each resting card peeks out from under the one above it. */
const PEEK = 5;
/** Cards visible in the resting deck, the front one included. */
const DECK = 3;

export function JobsStack() {
  const t = useT();
  const { jobs, attention, beats } = useSignals();
  const [open, setOpen] = useState(false);

  const cards = buildCards({ jobs, attention, beats });
  // One card is the resting state; there is nothing to fan out from a deck of
  // one, and a hover affordance that does nothing is worse than none.
  const canOpen = cards.length > 1;
  const expanded = open && canOpen;

  const height = expanded
    ? cards.length * CARD_H + (cards.length - 1) * GAP
    : CARD_H + Math.min(cards.length - 1, DECK - 1) * PEEK;

  return (
    <div
      className={cn("crm-stack", expanded && "is-open")}
      style={{ height }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      {cards.map((card, i) => {
        // Resting: tucked under the card in front, shrinking and fading with
        // depth. Expanded: a plain column. Anything past the third card is
        // parked behind the deck rather than unmounted, so opening does not
        // pop new elements into existence.
        const depth = Math.min(i, DECK - 1);
        const style = expanded
          ? { transform: `translateY(${i * (CARD_H + GAP)}px)`, opacity: 1, zIndex: cards.length - i }
          : {
              transform: `translateY(${depth * PEEK}px) scale(${1 - depth * 0.04})`,
              opacity: i === 0 ? 1 : i < DECK ? 0.55 - (depth - 1) * 0.25 : 0,
              zIndex: cards.length - i,
            };

        return (
          <div key={card.key} className="crm-stack-c" style={style} aria-hidden={!expanded && i > 0}>
            <JobFace card={card} t={t} />
          </div>
        );
      })}
    </div>
  );
}
